/**
 * Image Format Converter Utility
 * Handles conversion of modern image formats (HEIC, AVIF, WebP) to web-compatible formats
 */

// Supported input formats that need conversion
const CONVERSION_FORMATS = [
  'image/heic',
  'image/heif',
  'image/avif',
  'image/webp' // Optional conversion for better compatibility
];

// Target format for conversion
const TARGET_FORMAT = 'image/jpeg';
const TARGET_QUALITY = 0.85; // 85% quality for good balance

/**
 * Check if a file needs format conversion
 */
export const needsFormatConversion = (file: File): boolean => {
  return CONVERSION_FORMATS.includes(file.type.toLowerCase());
};

/**
 * Get user-friendly format name
 */
export const getFormatName = (mimeType: string): string => {
  const formatMap: Record<string, string> = {
    'image/heic': 'HEIC',
    'image/heif': 'HEIF', 
    'image/avif': 'AVIF',
    'image/webp': 'WebP',
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
    'image/gif': 'GIF'
  };
  
  return formatMap[mimeType.toLowerCase()] || 'Okänt format';
};

/**
 * Convert image file to web-compatible format using Canvas API
 */
export const convertImageFormat = async (
  file: File,
  targetFormat: string = TARGET_FORMAT,
  quality: number = TARGET_QUALITY
): Promise<File> => {
  return new Promise((resolve, reject) => {
    // Create image element
    const img = new Image();
    
    img.onload = () => {
      try {
        // Create canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Kunde inte skapa canvas context'));
          return;
        }
        
        // Set canvas dimensions to match image
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        
        // Draw image to canvas
        ctx.drawImage(img, 0, 0);
        
        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Kunde inte konvertera bilden'));
              return;
            }
            
            // Create new file with converted format
            const convertedFile = new File(
              [blob],
              file.name.replace(/\.[^/.]+$/, '.jpg'), // Change extension to .jpg
              {
                type: targetFormat,
                lastModified: file.lastModified
              }
            );
            
            resolve(convertedFile);
          },
          targetFormat,
          quality
        );

        // Clean up
        URL.revokeObjectURL(img.src);

      } catch (error) {
        // Also revoke on the canvas/drawImage failure path so a throw here
        // doesn't leak the object URL (the success and onerror paths already
        // revoke).
        URL.revokeObjectURL(img.src);
        reject(new Error(`Konverteringsfel: ${error instanceof Error ? error.message : 'Okänt fel'}`));
      }
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error(`Kunde inte läsa ${getFormatName(file.type)}-filen. Filen kan vara skadad.`));
    };
    
    // Create object URL and load image
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Process multiple files, converting formats as needed
 */
export const processImageFiles = async (
  files: File[],
  onProgress?: (current: number, total: number, fileName: string) => void
): Promise<File[]> => {
  const processedFiles: File[] = [];
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    
    if (onProgress) {
      onProgress(i + 1, files.length, file.name);
    }
    
    try {
      if (needsFormatConversion(file)) {
        console.log(`🔄 Konverterar ${getFormatName(file.type)}: ${file.name}`);
        const convertedFile = await convertImageFormat(file);
        processedFiles.push(convertedFile);
        console.log(`✅ Konverterad till JPEG: ${convertedFile.name}`);
      } else {
        // File doesn't need conversion
        processedFiles.push(file);
      }
    } catch (error) {
      console.error(`❌ Konverteringsfel för ${file.name}:`, error);
      throw new Error(`Kunde inte bearbeta ${file.name}: ${error instanceof Error ? error.message : 'Okänt fel'}`);
    }
  }
  
  return processedFiles;
};

/**
 * Validate image file size and format
 */
export const validateImageFile = (file: File, maxSizeMB: number = 10): { valid: boolean; error?: string } => {
  // Check if it's an image
  if (!file.type.startsWith('image/')) {
    return {
      valid: false,
      error: 'Endast bildfiler stöds'
    };
  }
  
  // Check file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `Bilden är för stor. Max storlek är ${maxSizeMB}MB`
    };
  }
  
  return { valid: true };
};

/**
 * Get comprehensive file information
 */
export const getFileInfo = (file: File) => {
  return {
    name: file.name,
    size: file.size,
    sizeFormatted: formatFileSize(file.size),
    type: file.type,
    formatName: getFormatName(file.type),
    needsConversion: needsFormatConversion(file),
    lastModified: new Date(file.lastModified)
  };
};

/**
 * Format file size in human-readable format
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

/**
 * Check browser support for image formats
 */
export const checkFormatSupport = async () => {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  
  const formats = {
    webp: canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0,
    avif: canvas.toDataURL('image/avif').indexOf('data:image/avif') === 0,
    heic: false, // HEIC is generally not supported in browsers
    heif: false  // HEIF is generally not supported in browsers
  };
  
  return formats;
}; 